import React from 'react';
import { Container, Typography, Box } from '@mui/material';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { posts } from './posts/posts';
import CodeBlock from './codeBlock';


const BlogArticle = () => {
  const { slug } = useParams();

  const article = posts.find((post) => post.slug === slug);

  if (!article) {
    return (
      <Container maxWidth="md" sx={{ marginTop: '40px' }}>
        <Typography
          variant="h6"
          align="center"
          sx={{
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontWeight: 600,
            color: '#2C3E50',
            marginBottom: '20px',
          }}
        >
          Article not found
        </Typography>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="md"
      sx={{ marginTop: '40px', padding: { xs: '20px', md: '40px' } }}
    >
      <Box
        sx={{
          padding: '20px',
          backgroundColor: '#fff',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontWeight: 700,
            color: '#2C3E50',
            marginBottom: '20px',
          }}
        >
          {article.title}
        </Typography>

        <img
          src={article.imageURL}
          alt={article.title}
          style={{
            width: '100%',
            height: 'auto',
            marginBottom: '20px',
            borderRadius: '8px',
          }}
        />

        <ReactMarkdown
          children={article.content}
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ node, ...props }) => (
              <Typography variant="h4" sx={{ fontWeight: 700, marginTop: '20px' }} {...props} />
            ),
            h2: ({ node, ...props }) => (
              <Typography variant="h5" sx={{ fontWeight: 600, marginTop: '20px' }} {...props} />
            ),
            h3: ({ node, ...props }) => (
              <Typography variant="h6" sx={{ fontWeight: 600, marginTop: '20px' }} {...props} />
            ),
            p: ({ node, ...props }) => (
              <Typography variant="body1" sx={{ marginBottom: '16px' }} {...props} />
            ),
            li: ({ node, ordered, ...props }) => (
              <li>
                <Typography component="span" variant="body1" {...props} />
              </li>
            ),
            a: ({ href, children }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#0b74c1',
                  textDecoration: 'underline',
                  fontWeight: 500,
                }}
              >
                {children}
              </a>
            ),
            code: CodeBlock,
            img: ({ src, alt, ...props }) => (
              <Box
                component="img"
                src={src}
                alt={alt}
                sx={{
                  maxWidth: '500px',
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  margin: '20px auto',
                  borderRadius: '8px',
                }}
                {...props}
              />
            ),
            table: ({ children }) => (
              <Box
                component="table"
                sx={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  marginBottom: '24px',
                }}
              >
                {children}
              </Box>
            ),
            thead: ({ children }) => <thead>{children}</thead>,
            tbody: ({ children }) => <tbody>{children}</tbody>,
            tr: ({ children }) => <Box component="tr">{children}</Box>,
            th: ({ children }) => (
              <Box
                component="th"
                sx={{
                  border: '1px solid #ccc',
                  backgroundColor: '#f9f9f9',
                  padding: '10px',
                  fontWeight: 600,
                  textAlign: 'left',
                }}
              >
                {children}
              </Box>
            ),
            td: ({ children }) => (
              <Box
                component="td"
                sx={{
                  border: '1px solid #ddd',
                  padding: '10px',
                  textAlign: 'left',
                }}
              >
                {children}
              </Box>
            ),
          }}
        />
      </Box>
    </Container>
  );
};

export default BlogArticle;
